import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectProps {
  label?: string;
  value?: string | number | string[];
  onChange: (value: string | number | string[]) => void;
  options: SelectOption[] | Record<string, string>;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  multiple?: boolean;
  /** When multiple: 'list' = show all labels; 'count' = show first label + (+N) for rest */
  multiDisplayFormat?: 'list' | 'count';
  required?: boolean;
  name?: string;
  id?: string;
}

const SelectContainer = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

const Label = styled.label<{ $error: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$error ? props.theme.destructive : props.theme.foreground};
`;

const SelectButton = styled.button<{
  $error: boolean;
  $disabled: boolean;
  $size: string;
  $fullWidth: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      default: return '0.5rem 0.875rem';
    }
  }};
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1rem';
      default: return '0.875rem';
    }
  }};
  background: ${props => props.theme.input || props.theme.background};
  border: 1px solid ${props => props.$error ? props.theme.destructive : props.theme.border};
  border-radius: ${props => props.theme.radius};
  color: ${props => props.theme.foreground};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.6 : 1};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.ring || props.theme.primary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.ring || props.theme.primary};
    box-shadow: 0 0 0 2px ${props => (props.theme.ring || props.theme.primary) + '40'};
  }
`;

const SelectDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10000;
  max-height: 200px;
  overflow-y: auto;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  margin-top: 4px;
`;

const SelectOption = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${props => props.$selected ? `${props.theme.primary}10` : props.theme.card};
  color: ${props => props.$selected ? props.theme.primary : props.theme.foreground};
  
  &:hover {
    background: ${props => props.$selected ? `${props.theme.primary}20` : props.theme.muted};
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const HelperText = styled.span<{ $error: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$error ? props.theme.destructive : props.theme.mutedForeground};
`;

const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  error = false,
  helperText,
  size = 'md',
  fullWidth = false,
  multiple = false,
  multiDisplayFormat = 'list',
  required = false,
  name,
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert options to array format if it's an object
  const optionsArray: SelectOption[] = Array.isArray(options) 
    ? options 
    : Object.entries(options).map(([value, label]) => ({ value, label }));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      if (multiple && Array.isArray(value)) {
        const labels = value.map(v => {
          const option = optionsArray.find(opt => opt.value === v);
          return option?.label || '';
        }).filter(Boolean);
        if (multiDisplayFormat === 'count' && labels.length > 1) {
          setSelectedLabel(`${labels[0]} (+${labels.length - 1})`);
        } else {
          setSelectedLabel(labels.join(', '));
        }
      } else {
        const option = optionsArray.find(opt => opt.value === value);
        setSelectedLabel(option?.label || '');
      }
    } else {
      setSelectedLabel('');
    }
  }, [value, optionsArray, multiple, multiDisplayFormat]);

  const handleSelect = (optionValue: string | number) => {
    if (multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(optionValue as string)
        ? currentValue.filter(v => v !== optionValue)
        : [...currentValue, optionValue as string];
      onChange(newValue);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <SelectContainer ref={containerRef} $fullWidth={fullWidth}>
      {label && (
        <Label $error={error}>
          {label}
          {required && <span style={{ color: 'var(--destructive)' }}> *</span>}
        </Label>
      )}
      
      <div style={{ position: 'relative' }}>
        <SelectButton
          type="button"
          $error={error}
          $disabled={disabled}
          $size={size}
          $fullWidth={fullWidth}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          name={name}
          id={id}
        >
          <span>{selectedLabel || placeholder}</span>
          <ChevronDown size={16} style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} />
        </SelectButton>
        
        <SelectDropdown $isOpen={isOpen}>
          {optionsArray.map((option) => (
            <SelectOption
              key={option.value}
              $selected={multiple 
                ? Array.isArray(value) && value.includes(option.value as string)
                : option.value === value
              }
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </SelectOption>
          ))}
        </SelectDropdown>
      </div>
      
      {helperText && (
        <HelperText $error={error}>
          {helperText}
        </HelperText>
      )}
    </SelectContainer>
  );
};

export default Select; 